import { gql, MutationResult, useMutation } from '@apollo/client';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ReminderJourneyOverride } from '../../../../generated/event.types';
import { succesToast, errorToast } from 'basics/utils/toast';
import { extractApolloErrorDetails } from 'services/graphql/extractApolloErrorDetails';
import Logger from 'services/logger';

export const setEventReminderOverridesMutation = gql`
  mutation setEventReminderOverrides($input: SetEventReminderOverridesInput!) {
    setEventReminderOverrides(input: $input) {
      _id
      title
      reminderConfig {
        overrides {
          journeyId
          enabled
        }
      }
    }
  }
`;

type SetEventReminderOverridesInput = {
  eventId: string;
  overrides: ReminderJourneyOverride[];
};

type ReminderConfig = {
  overrides: ReminderJourneyOverride[];
};

type EventWithReminderConfig = {
  _id: string;
  title: string;
  reminderConfig: ReminderConfig;
};

type ResultType = {
  setEventReminderOverrides: EventWithReminderConfig;
};

type UseSetEventReminderOverridesMutation = [
  (input: SetEventReminderOverridesInput) => Promise<EventWithReminderConfig | null>,
  MutationResult<ResultType>,
];

export const useSetEventReminderOverridesMutation = (): UseSetEventReminderOverridesMutation => {
  const { t } = useTranslation();
  const [setEventReminderOverrides, state] = useMutation<ResultType>(setEventReminderOverridesMutation);

  const executeMutation = useCallback(async (input: SetEventReminderOverridesInput) => {
    try {
      const { data } = await setEventReminderOverrides({
        variables: { input },
      });
      succesToast(t('reminder_schedule_save_success'));

      return data?.setEventReminderOverrides || null;
    } catch (error: unknown) {
      if (error instanceof Error) {
        const customContext = extractApolloErrorDetails(error, { eventId: input.eventId });
        Logger.logError({
          error,
          customContext,
          transactionName: 'setEventReminderOverrides.ts - useSetEventReminderOverridesMutation',
        });
      }
      errorToast(t('reminder_schedule_save_error'));
      return null;
    }
  }, [setEventReminderOverrides, t]);

  return [executeMutation, state];
};
